import type {
  DraftMessageId,
  SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import type { SupportTicketDetailResponse } from "@redotech/effect-api-shared/rpc/support-ticket";
import type { RedoMerchantRpcClient } from "@redotech/merchant-sdk/rpc/client";
import type { RedoMerchantClient } from "@redotech/redo-merchant-app-common/client";
import { getTeamReturns } from "@redotech/redo-merchant-app-common/client/team";
import type { EffectApiContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import type { MerchantAppReturn } from "@redotech/redo-model/return";
import type { SupportTicketDraftsApi } from "@redotech/redo-support-v2/drafts/support-ticket-drafts-collection";
import type { SupportTicketListApi } from "@redotech/redo-support-v2/query/support-ticket-list-collection";
import type {
  OrderWithPickStatus,
  SupportTicketRelatedRecordsApi,
} from "@redotech/redo-support-v2/query/support-ticket-related-records-collections";
import type {
  AccountTagsResponse,
  SupportTicketTagsApi,
} from "@redotech/redo-support-v2/query/support-ticket-tags-collections";
import type { SupportTicketTranscriptApi } from "@redotech/redo-support-v2/query/support-ticket-transcript-collections";
import { queryOptions, type QueryClient } from "@tanstack/react-query";
import { profileSupportTicketRequest } from "../profiling/support-ticket-load-profiler";
import { catchApiErrors } from "./error-handling";

export interface CreateMerchantSupportTicketDataSourceOptions {
  effectApi: EffectApiContext;
  queryClient?: QueryClient;
  rpcClient?: RedoMerchantRpcClient;
  merchantClient?: RedoMerchantClient;
}

export interface MerchantSupportTicketDataSource {
  list: SupportTicketListApi;
  transcript: SupportTicketTranscriptApi;
  tags: SupportTicketTagsApi;
  relatedRecords: SupportTicketRelatedRecordsApi;
  drafts: SupportTicketDraftsApi;
  getSupportTicketSummary(
    supportTicketId: SupportTicketId,
  ): Promise<{ summary: string | null }>;
}

const SUPPORT_TICKET_DETAIL_BUNDLE_STALE_TIME_MS = 5 * 60 * 1000;

export const supportTicketDetailBundleBaseQueryKey = [
  "support-ticket-detail-bundle",
] as const;

export function supportTicketDetailBundleQueryKey(
  supportTicketId: SupportTicketId,
) {
  return [...supportTicketDetailBundleBaseQueryKey, supportTicketId] as const;
}

async function fetchSupportTicketDetailBundle(
  effectApi: EffectApiContext,
  supportTicketId: SupportTicketId,
): Promise<SupportTicketDetailResponse> {
  const client = effectApi.getClient();
  return profileSupportTicketRequest(
    "getSupportTicketDetail",
    { supportTicketId },
    () =>
      effectApi.runPromise(
        catchApiErrors(
          client.supportTicket.getSupportTicketDetail({
            path: { id: supportTicketId },
          }),
        ),
      ),
  );
}

export function supportTicketDetailBundleQueryOptions(
  effectApi: EffectApiContext,
  supportTicketId: SupportTicketId,
) {
  return queryOptions({
    queryKey: supportTicketDetailBundleQueryKey(supportTicketId),
    queryFn: () => fetchSupportTicketDetailBundle(effectApi, supportTicketId),
    staleTime: SUPPORT_TICKET_DETAIL_BUNDLE_STALE_TIME_MS,
  });
}

export function createMerchantSupportTicketDataSource({
  effectApi,
  queryClient,
  rpcClient,
  merchantClient,
}: CreateMerchantSupportTicketDataSourceOptions): MerchantSupportTicketDataSource {
  const client = effectApi.getClient();
  const getBundle = (supportTicketId: SupportTicketId) =>
    queryClient
      ? queryClient.fetchQuery(
          supportTicketDetailBundleQueryOptions(effectApi, supportTicketId),
        )
      : fetchSupportTicketDetailBundle(effectApi, supportTicketId);
  const invalidateBundle =
    <T>(supportTicketId: SupportTicketId) =>
    (result: T) => {
      void queryClient?.invalidateQueries({
        queryKey: supportTicketDetailBundleQueryKey(supportTicketId),
      });
      return result;
    };

  return {
    list: {
      getSupportTicket: (supportTicketId) =>
        getBundle(supportTicketId).then((bundle) => bundle.supportTicket),
      listSupportTickets: (urlParams) =>
        profileSupportTicketRequest("listSupportTickets", urlParams, () =>
          effectApi.runPromise(
            catchApiErrors(
              client.supportTicket.listSupportTickets({ urlParams }),
            ),
          ),
        ),
      updateSupportTicket: (supportTicketId, payload) =>
        profileSupportTicketRequest(
          "updateSupportTicket",
          { supportTicketId, fields: Object.keys(payload) },
          () =>
            effectApi
              .runPromise(
                catchApiErrors(
                  client.supportTicket.updateSupportTicket({
                    path: { id: supportTicketId },
                    payload,
                  }),
                ),
              )
              .then(invalidateBundle(supportTicketId))
              .then(() => undefined),
        ),
    },
    transcript: {
      getThreads: (supportTicketId) =>
        getBundle(supportTicketId).then((bundle) => bundle.threads),
      getMessages: (supportTicketId) =>
        getBundle(supportTicketId).then((bundle) => bundle.messages),
      getTimelineEvents: (supportTicketId) =>
        profileSupportTicketRequest(
          "getSupportTicketTimelineEvents",
          { supportTicketId },
          () =>
            effectApi.runPromise(
              catchApiErrors(
                client.supportTicket.getSupportTicketTimelineEvents({
                  path: { id: supportTicketId },
                }),
              ),
            ),
        ),
    },
    tags: {
      getAccountTags: async (): Promise<AccountTagsResponse> => {
        const result = await profileSupportTicketRequest(
          "getAccountTags",
          {},
          () =>
            effectApi.runPromise(catchApiErrors(client.tag.getAccountTags({}))),
        );
        return { tags: [...result.tags], tagGroups: [...result.tagGroups] };
      },
    },
    relatedRecords: {
      getOrdersByEmail: async (email) => {
        if (!rpcClient) {
          throw new Error(
            "Related order lookup requires a merchant RPC client",
          );
        }
        return profileSupportTicketRequest(
          "getTeamOrdersByEmail",
          { hasEmail: email.length > 0 },
          async () => {
            const result = await rpcClient.getTeamOrdersByEmail({ email });
            return result.orders as OrderWithPickStatus[];
          },
        );
      },
      getReturnsByEmail: async (email): Promise<MerchantAppReturn[]> => {
        if (!merchantClient) {
          throw new Error("Related return lookup requires a merchant client");
        }
        return profileSupportTicketRequest(
          "getTeamReturns",
          { hasEmail: email.length > 0 },
          () =>
            getTeamReturns(merchantClient, { email }) as Promise<
              MerchantAppReturn[]
            >,
        );
      },
    },
    drafts: {
      getDrafts: (supportTicketId: SupportTicketId) =>
        getBundle(supportTicketId).then((bundle) => bundle.drafts),
      saveDraft: (supportTicketId, payload) =>
        profileSupportTicketRequest(
          "saveSupportTicketDraft",
          { supportTicketId, threadId: payload.threadId },
          () =>
            effectApi
              .runPromise(
                catchApiErrors(
                  client.supportTicket.saveSupportTicketDraft({
                    path: { id: supportTicketId },
                    payload,
                  } as Parameters<
                    typeof client.supportTicket.saveSupportTicketDraft
                  >[0]),
                ),
              )
              .then(invalidateBundle(supportTicketId))
              .then(() => undefined),
        ),
      deleteDraft: (supportTicketId, draftId: DraftMessageId) =>
        profileSupportTicketRequest(
          "deleteSupportTicketDraft",
          { supportTicketId, draftId },
          () =>
            effectApi
              .runPromise(
                catchApiErrors(
                  client.supportTicket.deleteSupportTicketDraft({
                    path: { id: supportTicketId, draftId },
                  }),
                ),
              )
              .then(invalidateBundle(supportTicketId)),
        ),
    },
    getSupportTicketSummary: (supportTicketId: SupportTicketId) =>
      getBundle(supportTicketId).then((bundle) => bundle.summary),
  };
}
